import React, { useEffect } from "react";
import Hero from "../components/Hero/Hero";
import SectionDescription from "../components/SectionDescription/SectionDescription";
import EditorialCard from '../components/EditorialCard/EditorialCard';
import TextLoop from '../components/TextLoop/TextLoop'
import { Col, Container, Row } from "../components/ui/GridSystem/GridSystem";
import Constants from "../config/Constants";
import { Wrapper } from "../components/ui/Wrapper/Wrapper";
import {Text} from '../components/ui/Typography/Typography';
import styled, { keyframes } from "styled-components";

const anmTxtCta = keyframes`
    100% {
        transform: translateY(-100%);
    }
`;

const anmTxtCtaMobile = keyframes`
    25% {
        transform: translateY(-20%);
    }
    50% {
        transform: translateY(0%);
    }
`;

const anmIconNotify = keyframes`
    20% {
        transform: rotate(15deg);
    }
    25% {
        transform: rotate(10deg);
    }
    30% {
        transform: rotate(15deg);
    }
    35% {
        transform: rotate(0deg);
    }
    40% {
        transform: rotate(-15deg);
    }
    45% {
        transform: rotate(-10deg);
    }
    50% {
        transform: rotate(-15deg);
    }
    55% {
        transform: rotate(0deg);
    }
`;

const SpanText = styled('span')`
    color: ${({theme}) => theme.colors.main};
    font-size: 3rem;
    line-height: 130%;
    font-weight: 700;
    display: inline-block;

    @media ${({theme}) => theme.mediaQueries.breakpointMd} {
        margin-top: 40px;
        font-size: 2rem;
    }

    .notify-icon {
        display: inline-block;
        animation: ${anmIconNotify} 1.5s ease-in-out infinite;
        @media ${({theme}) => theme.mediaQueries.breakpointMd} {
            animation-play-state: paused;
        }
    }

    .link {
        display: inline-block;
        text-decoration: none;
        color: ${({theme}) => theme.colors.main};
        position: relative;

        &:hover {
            div {
                &:first-child {
                    span {
                        animation: ${anmTxtCta} 220ms ease-in-out;
                        &:nth-child(1) { animation-delay: 0ms }
                        &:nth-child(2) { animation-delay: 25ms }
                        &:nth-child(3) { animation-delay: 45ms }
                        &:nth-child(4) { animation-delay: 70ms }
                        &:nth-child(5) { animation-delay: 95ms }
                        &:nth-child(6) { animation-delay: 120ms }
                        &:nth-child(7) { animation-delay: 145ms }
                        &:nth-child(8) { animation-delay: 170ms }
                        &:nth-child(9) { animation-delay: 195ms }
                        &:nth-child(10) { animation-delay: 220ms }
                    }
                }
                &:last-child {
                    transform: translateY(-50%);
                }
            } 
        }
        div {
            overflow: hidden;
            &:first-child {
                display: flex;
                overflow: hidden;
                text-shadow: 0 4rem 0 ${({theme}) => theme.colors.main};
                z-index: 1;
                position: relative;
                span {
                    transition: all 100ms ease-in-out;
                    @media ${({theme}) => theme.mediaQueries.breakpointMd} {
                        animation: ${anmTxtCtaMobile} 2s ease-in-out infinite;
                        &:nth-child(1) { animation-delay: 0ms }
                        &:nth-child(2) { animation-delay: 30ms }
                        &:nth-child(3) { animation-delay: 55ms }
                        &:nth-child(4) { animation-delay: 80ms }
                        &:nth-child(5) { animation-delay: 105ms }
                        &:nth-child(6) { animation-delay: 130ms }
                        &:nth-child(7) { animation-delay: 155ms }
                        &:nth-child(8) { animation-delay: 180ms }
                        &:nth-child(9) { animation-delay: 205ms }
                        &:nth-child(10) { animation-delay: 230ms }
                    }
                }
            }
            &:last-child {
                position: absolute;
                pointer-events: none;
                right: 0;
                left: 0;
                width: 100%;
                height: 4px;
                z-index: 0;
                background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
                transition: all 200ms ease-in-out;
            }
        }
    }
`;

const PageHome = () => {
    useEffect(() => {
        document.querySelectorAll('.link').forEach(link => {
            link.innerHTML = '<div><span>' + link!.textContent!.trim().split('').join('</span><span>') + '</span></div>'
            link.querySelectorAll('span').forEach(s => s.textContent === ' ' ? '&nbsp;' : s.textContent)
            link.insertAdjacentHTML('beforeend', '<div><svg preserveAspectRatio="none" viewBox="0 0 192 5"><path d="M191.246 4H129C129 4 127.781 4.00674 127 4C114.767 3.89447 108.233 1 96 1C83.7669 1 77.2327 3.89447 65 4C64.219 4.00674 63 4 63 4H0.751923" /></svg></div>')
        });
    }, []);
    
    return (
        <>
            <Hero />
            <SectionDescription />
            <TextLoop/>
            {renderSectionDays()}
            {renderTexIG()}
        </>
    );

    function renderSectionDays() {
        const cards = Constants.DAYS.sort((a,b) => Number(renderNextDate(a.title)) - Number(renderNextDate(b.title))).map((x, i) => (
            <Col key={i} xl={4} lg={4} md={4} sm={12} xs={12}>
                <EditorialCard
                    nextDay={renderNextDate(x.title)}
                    title={x.title}
                    text={x.text}
                    color={x.color}
                    index={i.toString()}
                />
            </Col>
        ));
        return (
            <Wrapper marginBottom='80px'>
                <Container>
                    <Row>
                        {cards}
                    </Row>
                </Container>
            </Wrapper>
        );
    }

    function renderNextDate(dayIndex: string) {
        const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
        const today = new Date();
        today.setDate(today.getDate() + (days.indexOf(dayIndex) - 1 - today.getDay() + 7) % 7 + 1);
        return today.toLocaleString().split('/')[0];
    }

    function renderTexIG(){
        return (
            <Wrapper width={'100%'} padding={'60px 0px'} backgroundColor={'#fff'}>
                <Container>
                    <Row>
                        <Col xl={3} lg={3} md={3} sm={12} xs={12}>
                            <Text grey margin={'20px 0 0 0'} dangerouslySetInnerHTML={{__html: Constants.DESCRIPTION_TEXT_NOTIFY_IG_1}}/>
                        </Col>
                        <Col offset={{where: ['xl', 'lg'], value: 1}} xl={8} lg={8} md={12} sm={12} xs={12}>
                            <SpanText dangerouslySetInnerHTML={{__html: Constants.DESCRIPTION_TEXT_NOTIFY_IG_2}}/>
                        </Col>
                    </Row>
                </Container>
            </Wrapper>
        );
    }
}

export default PageHome;